import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { MaterialIcon } from '@lapo/ui';
import { PageType, fuzzyMatchIndices } from '@lapo/core';
import { displayTitle, searchNotes, type LocalNote } from './noteIndex.js';
import { loadAdaptiveHistory, recordPick } from './adaptiveHistory.js';
import type { ContentHitDTO, PaletteGroupDTO, PaletteHitDTO } from './auth/apiClient.js';
import { NoteIcon } from './NoteIcon.js';

/** How long to wait after the last keystroke before hitting the server for content/corpus matches. */
const CONTENT_DEBOUNCE_MS = 180;

/**
 * LENS-01 — cross-corpus groups shown in Cmd+K. Only corpora Lapo can actually NAVIGATE to belong
 * here (a palette row must go somewhere); `contact` deep-links to the contact page. Email/signal/task
 * rows are deferred until per-item routes exist — they stay in the full SearchView's `in:` filters
 * (see lens-decision-log DL-3). This list is the single knob to widen the palette later.
 */
const PALETTE_CORPORA = ['contact'];

/** Default visible rows per group before "Show more" (LENS-02). */
const GROUP_LIMITS = { current: 4, notes: 8, content: 6, people: 3 } as const;

/** Escape a string for literal use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Render `text` with the query's terms highlighted, so a preview row shows WHY it matched. Terms are
 * the free-text tokens of the query (≥2 chars); matching is case-insensitive and preserves the original
 * casing. Longest-first alternation so "programming" wins over a stray "program" substring.
 */
function highlight(text: string, query: string): ReactNode {
  const terms = [...new Set(query.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length >= 2))]
    .sort((a, b) => b.length - a.length)
    .slice(0, 8);
  if (terms.length === 0) return text;
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'giu');
  // split() with one capture group interleaves [between, match, between, match, …] — odd indices match.
  return text.split(re).map((part, i) => (i % 2 === 1 ? <mark key={i} className="lapo-search-mark">{part}</mark> : part));
}

/**
 * LENS-05 — highlight a TITLE by the exact chars the fuzzy matcher scored (so a title row shows why it
 * matched, not just content rows). Uses the shared `fuzzyMatchIndices` (same walk as the ranker); when
 * the query isn't a subsequence of the title it falls back to the substring highlighter above.
 */
function highlightTitle(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const idx = fuzzyMatchIndices(text, q);
  if (!idx || idx.length === 0) return highlight(text, q);
  const marked = new Set(idx);
  const out: ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const on = marked.has(i);
    let j = i;
    while (j < text.length && marked.has(j) === on) j++;
    const slice = text.slice(i, j);
    out.push(on ? <mark key={i} className="lapo-search-mark">{slice}</mark> : <span key={i}>{slice}</span>);
    i = j;
  }
  return out;
}

/** The contact id behind a `contact:<id>` palette hit. */
function contactIdOf(hit: PaletteHitDTO): string {
  return hit.id.startsWith('contact:') ? hit.id.slice('contact:'.length) : hit.id;
}

/** A single selectable row, unified so keyboard nav (↑/↓/Enter) spans every group linearly. */
type Row =
  | { kind: 'note'; note: LocalNote }
  | { kind: 'content'; hit: ContentHitDTO; note?: LocalNote; group: 'current' | 'content' }
  | { kind: 'person'; hit: PaletteHitDTO }
  | { kind: 'create' };

/** A rendered group: a header + its (already limited) selectable rows and the total available. */
interface Group {
  key: 'current' | 'notes' | 'content' | 'people' | 'create';
  label: string;
  rows: Row[];
  total: number;
  expandable: boolean;
}

/**
 * Global search palette (Cmd/Ctrl+K, or the toolbar search icon). Grouped, Logseq-style (LENS-02): an
 * optional "Current note" group (blocks within the open page), instant local TITLE matches, a debounced
 * server CONTENT search, and a cross-corpus "People" group — each with its own header, count, and
 * "Show more". Under the hood it stays ONE flat linear row list so keyboard nav is unchanged: ↑/↓ move
 * across groups, Enter activates, Shift+Enter opens in a new tab (peek), Esc clears then closes. When a
 * non-empty query matches nothing, the last row becomes "Create a document called …".
 */
export function SearchModal({
  open,
  notes,
  onClose,
  onOpen,
  onCreate,
  searchContent,
  localContent,
  currentNoteId,
  searchInNote,
  searchPalette,
  onOpenContact,
  onPeek,
}: {
  open: boolean;
  notes: LocalNote[];
  onClose: () => void;
  /** Open a note; `blockId` (from a content hit) scrolls to the exact block that surfaced the match. */
  onOpen: (noteId: string, blockId?: string) => void;
  onCreate: (title: string) => void;
  /** Server-backed body search; omitted (e.g. signed out) falls back to title-only. */
  searchContent?: (query: string, signal?: AbortSignal) => Promise<ContentHitDTO[]>;
  /** FIND-08: instant on-device body search over the working set. Merged ahead of the server hits. */
  localContent?: (query: string) => ContentHitDTO[];
  /** LENS-03: the note currently open, so a "Current note" group can scope search to this page. */
  currentNoteId?: string;
  /** LENS-03: instant on-device search WITHIN one note (multiple block hits), for the current-note group. */
  searchInNote?: (noteId: string, query: string) => ContentHitDTO[];
  /** LENS-03: grouped cross-corpus server search (People today). */
  searchPalette?: (query: string, corpora: string[], signal?: AbortSignal) => Promise<PaletteGroupDTO[]>;
  /** LENS-03: navigate to a contact (person row). Omitted → person rows are hidden. */
  onOpenContact?: (contactId: string) => void;
  /** LENS-05: Shift+Enter "peek" — open elsewhere without losing the palette (new tab on web). */
  onPeek?: (noteId: string, blockId?: string) => void;
}): ReactElement | null {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [content, setContent] = useState<ContentHitDTO[]>([]);
  const [people, setPeople] = useState<PaletteHitDTO[]>([]);
  const [expanded, setExpanded] = useState<Set<Group['key']>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef('');
  queryRef.current = query;

  // Reset to a clean palette every time it opens, and focus the field.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    setContent([]);
    setPeople([]);
    setExpanded(new Set());
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Desktop: Escape from anywhere (not just the field) — STAGED (LENS-05): a non-empty query clears
  // first, only an empty query closes. A document-level listener (mobile Back-button dismissal lives in
  // App). queryRef keeps this subscribed once instead of re-binding per keystroke.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (queryRef.current.trim()) {
        e.preventDefault();
        setQuery('');
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const trimmed = query.trim();
  // Adaptive history is loaded once per open (it only changes on pick, which closes the palette).
  const adaptive = useMemo(() => (open ? loadAdaptiveHistory() : []), [open]);
  const titleResults = useMemo(
    () => searchNotes(notes, query, Date.now(), adaptive).slice(0, 40),
    [notes, query, adaptive],
  );
  const byId = useMemo(() => new Map(notes.map((n) => [n.noteId, n])), [notes]);
  // Instant on-device body hits (working set), computed synchronously — no debounce, no network.
  const localHits = useMemo(
    () => (open && localContent && trimmed ? localContent(trimmed) : []),
    [open, localContent, trimmed],
  );
  // Instant on-device "search this page" hits for the current-note group.
  const currentHits = useMemo(
    () => (open && trimmed && currentNoteId && searchInNote ? searchInNote(currentNoteId, trimmed) : []),
    [open, trimmed, currentNoteId, searchInNote],
  );

  // Debounced server body search. Aborts the in-flight request on each keystroke.
  useEffect(() => {
    if (!open || !searchContent || !trimmed) {
      setContent([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchContent(trimmed, controller.signal)
        .then((hits) => {
          if (!controller.signal.aborted) setContent(hits);
        })
        .catch(() => {
          /* aborted or offline — leave content as-is; title matches still work */
        });
    }, CONTENT_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, searchContent, trimmed]);

  // Debounced cross-corpus (People) search, same abort discipline.
  useEffect(() => {
    if (!open || !searchPalette || !onOpenContact || !trimmed) {
      setPeople([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchPalette(trimmed, PALETTE_CORPORA, controller.signal)
        .then((groups) => {
          if (controller.signal.aborted) return;
          const contacts = groups.find((g) => g.corpus === 'contact');
          setPeople(contacts?.hits ?? []);
        })
        .catch(() => {
          /* aborted or offline — hide the group */
        });
    }, CONTENT_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, searchPalette, onOpenContact, trimmed]);

  // Group assembly (order = Current note → Pages/Recent → In content → People → Create). Content and
  // People are de-duped against notes already shown as title matches and against the current note, so a
  // page never appears twice.
  const shownTitleIds = useMemo(() => new Set(titleResults.map((n) => n.noteId)), [titleResults]);
  const contentHits = useMemo<ContentHitDTO[]>(() => {
    const seen = new Set(shownTitleIds);
    if (currentNoteId) seen.add(currentNoteId);
    const out: ContentHitDTO[] = [];
    for (const h of [...localHits, ...content]) {
      if (seen.has(h.noteId)) continue;
      seen.add(h.noteId);
      out.push(h);
    }
    return out;
  }, [localHits, content, shownTitleIds, currentNoteId]);

  const showCreate =
    trimmed.length > 0 && titleResults.length === 0 && contentHits.length === 0 && currentHits.length === 0 && people.length === 0;

  const groups = useMemo<Group[]>(() => {
    const out: Group[] = [];
    const take = (key: Group['key'], all: Row[], limit: number): Group | null => {
      if (all.length === 0) return null;
      const rows = expanded.has(key) ? all : all.slice(0, limit);
      return { key, label: '', rows, total: all.length, expandable: all.length > limit };
    };

    const currentGroup = take(
      'current',
      currentHits.map((hit) => ({ kind: 'content', hit, note: byId.get(hit.noteId), group: 'current' as const })),
      GROUP_LIMITS.current,
    );
    if (currentGroup) out.push({ ...currentGroup, label: 'Current note' });

    const notesGroup = take('notes', titleResults.map((note) => ({ kind: 'note', note })), GROUP_LIMITS.notes);
    if (notesGroup) out.push({ ...notesGroup, label: trimmed ? 'Pages' : 'Recent' });

    const contentGroup = take(
      'content',
      contentHits.map((hit) => ({ kind: 'content', hit, note: byId.get(hit.noteId), group: 'content' as const })),
      GROUP_LIMITS.content,
    );
    if (contentGroup) out.push({ ...contentGroup, label: 'In content' });

    const peopleGroup = take('people', people.map((hit) => ({ kind: 'person', hit })), GROUP_LIMITS.people);
    if (peopleGroup) out.push({ ...peopleGroup, label: 'People' });

    if (showCreate) out.push({ key: 'create', label: '', rows: [{ kind: 'create' }], total: 1, expandable: false });
    return out;
  }, [currentHits, titleResults, contentHits, people, byId, expanded, trimmed, showCreate]);

  // Flatten to the single linear selectable list the keyboard model walks (headers are render-only).
  const flatRows = useMemo<Row[]>(() => groups.flatMap((g) => g.rows), [groups]);

  // Keep the highlight in range as results shrink/grow.
  useEffect(() => {
    setActive((i) => (flatRows.length === 0 ? 0 : Math.min(i, flatRows.length - 1)));
  }, [flatRows.length]);

  if (!open) return null;

  const activate = (index: number, shift = false): void => {
    const row = flatRows[index];
    if (!row) return;
    if (row.kind === 'create') {
      onCreate(trimmed);
      return;
    }
    if (row.kind === 'person') {
      onOpenContact?.(contactIdOf(row.hit));
      return;
    }
    const pickedId = row.kind === 'note' ? row.note.noteId : row.hit.noteId;
    // A content hit deep-links to the exact block that surfaced it; a title hit just opens the note.
    const blockId = row.kind === 'content' ? row.hit.blockId || undefined : undefined;
    // Reinforce the (query → pick) memory so this result floats up next time (FIND-02). Only for a
    // real query — an empty-query pick from the recents list carries no query signal.
    if (trimmed) recordPick(trimmed, pickedId);
    // Shift+Enter peeks (open elsewhere without losing the palette); plain Enter opens in place.
    if (shift && onPeek) onPeek(pickedId, blockId);
    else onOpen(pickedId, blockId);
  };

  /** Expand (or collapse) the group the active row currently sits in — Cmd/Ctrl+↓ / ↑. */
  const toggleActiveGroup = (expand: boolean): void => {
    let offset = 0;
    for (const g of groups) {
      if (active < offset + g.rows.length) {
        if (!g.expandable && expand) return;
        setExpanded((prev) => {
          const next = new Set(prev);
          if (expand) next.add(g.key);
          else next.delete(g.key);
          return next;
        });
        return;
      }
      offset += g.rows.length;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) toggleActiveGroup(true);
      else setActive((i) => (flatRows.length ? (i + 1) % flatRows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) toggleActiveGroup(false);
      else setActive((i) => (flatRows.length ? (i - 1 + flatRows.length) % flatRows.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatRows.length) activate(active, e.shiftKey);
    }
  };

  const toggleGroup = (key: Group['key']): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="lapo-search-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="lapo-search"
        role="dialog"
        aria-label="Search"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="lapo-search-field">
          <MaterialIcon name="search" size={20} />
          <input
            ref={inputRef}
            type="text"
            className="lapo-search-input"
            placeholder="Search titles and content…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search titles and content"
          />
          <kbd className="lapo-search-esc">Esc</kbd>
        </div>
        <ul className="lapo-search-results">
          {groups.map((group, gi) => {
            // The global (flat) index of this group's first row, so each row knows if it's active.
            const offset = groups.slice(0, gi).reduce((n, g) => n + g.rows.length, 0);
            return (
              <li key={group.key} className="lapo-search-group">
                {group.label ? (
                  <div className="lapo-search-section">
                    <span>{group.label}</span>
                    {group.expandable ? (
                      <button
                        type="button"
                        className="lapo-search-more"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => toggleGroup(group.key)}
                      >
                        {expanded.has(group.key) ? 'Show less' : `Show all ${group.total > 99 ? '99+' : group.total}`}
                      </button>
                    ) : (
                      <span className="lapo-search-count">{group.total > 99 ? '99+' : group.total}</span>
                    )}
                  </div>
                ) : null}
                <ul className="lapo-search-grouprows">
                  {group.rows.map((row, ri) => {
                    const i = offset + ri;
                    return (
                      <li key={rowKey(row, i)}>
                        <SearchRow row={row} query={trimmed} active={i === active} onHover={() => setActive(i)} onClick={(shift) => activate(i, shift)} />
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
          {!flatRows.length ? (
            <li className="lapo-search-empty">{trimmed ? 'No matches.' : 'Type to search your pages.'}</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

/** Stable React key for a selectable row. */
function rowKey(row: Row, i: number): string {
  if (row.kind === 'create') return '__create__';
  if (row.kind === 'note') return `note:${row.note.noteId}`;
  if (row.kind === 'person') return `person:${row.hit.id}`;
  return `content:${row.group}:${row.hit.noteId}:${row.hit.blockId ?? i}`;
}

/** One rendered result row. Split out so the map stays readable; purely presentational. */
function SearchRow({
  row,
  query,
  active,
  onHover,
  onClick,
}: {
  row: Row;
  query: string;
  active: boolean;
  onHover: () => void;
  onClick: (shift: boolean) => void;
}): ReactElement {
  const cls = `lapo-search-row${active ? ' lapo-active' : ''}`;
  const handle = (e: React.MouseEvent): void => onClick(e.shiftKey);

  if (row.kind === 'create') {
    return (
      <button type="button" className={`${cls} lapo-search-create`} onMouseEnter={onHover} onClick={handle}>
        <span className="lapo-search-emoji">
          <MaterialIcon name="add" size={18} />
        </span>
        <span className="lapo-search-title">Create a document called “{query}”</span>
      </button>
    );
  }

  if (row.kind === 'note') {
    return (
      <button type="button" className={cls} onMouseEnter={onHover} onClick={handle}>
        <span className="lapo-search-emoji">
          <NoteIcon icon={row.note.icon} type={row.note.type} />
        </span>
        <span className="lapo-search-title">{highlightTitle(displayTitle(row.note), query)}</span>
      </button>
    );
  }

  if (row.kind === 'person') {
    return (
      <button type="button" className={cls} onMouseEnter={onHover} onClick={handle}>
        <span className="lapo-search-emoji">
          <MaterialIcon name="person" size={18} />
        </span>
        <span className="lapo-search-body">
          <span className="lapo-search-title">{highlightTitle(row.hit.title || 'Contact', query)}</span>
          {row.hit.snippet ? <span className="lapo-search-snippet">{highlight(row.hit.snippet, query)}</span> : null}
        </span>
      </button>
    );
  }

  // Content hit (Logseq-style): lead with the MATCHED SNIPPET as the primary line, and put the page
  // name + heading path underneath as a faint breadcrumb "source" line. This keeps the actual result in
  // focus instead of a bare journal date — journals hold most captured text, so a title-first row just
  // shows "July 22, 2026" and buries the match. Falls back to a title-primary row when there's no
  // snippet (e.g. a semantic-only hit).
  const title = row.note ? displayTitle(row.note) : row.hit.title || 'Untitled';
  const icon = <NoteIcon icon={row.note?.icon} type={row.note?.type ?? (row.hit.type as PageType)} />;
  if (!row.hit.snippet) {
    return (
      <button type="button" className={cls} onMouseEnter={onHover} onClick={handle}>
        <span className="lapo-search-emoji">{icon}</span>
        <span className="lapo-search-title">{highlightTitle(title, query)}</span>
      </button>
    );
  }
  return (
    <button type="button" className={cls} onMouseEnter={onHover} onClick={handle}>
      <span className="lapo-search-emoji">{icon}</span>
      <span className="lapo-search-body">
        <span className="lapo-search-snippet-primary">{highlight(row.hit.snippet, query)}</span>
        <span className="lapo-search-source">
          {highlightTitle(title, query)}
          {row.hit.crumbs ? <span className="lapo-search-source-crumbs"> › {row.hit.crumbs}</span> : null}
        </span>
      </span>
    </button>
  );
}
